import React, { useEffect, useState, Fragment } from 'react';
import { Observer } from 'mobx-react';
import { Link, useHistory } from 'react-router-dom';
import { format } from 'date-fns';
import { Typography, Button } from '@material-ui/core';
import OpenInNew from '@material-ui/icons/OpenInNew';
import CirclePlusIcon from '@material-ui/icons/AddCircleOutline';
import FilterIcon from '@material-ui/icons/FilterList';
import {
  useQueryParams,
  NumberParam,
  StringParam,
  withDefault,
  JsonParam,
} from 'use-query-params';

import { useRootStore } from '../../../stores/BridgeProvider';
import storageService from '../../../services/storage.service';
import Spinner from '../../util/Spinner';
import { FlexDiv, WideBox } from '../../styles/styles';
import { dateSort } from '../../util/TableUtils';
import { isCompareObjects, safeEval } from '../../util/UtilFunctions';
import { CustomTable, FilterPlaceholder } from '../../common/CustomTable';
import { NumberHandler } from '../../util/NumberHandler';
import RenderPropModal from '../../dealMapper/common/RenderPropModal';
import FilterModal from './FilterModal';

const columns = ({ paymentStatuses }) => [
  {
    Header: 'Contact',
    accessor: 'ContactName',
    Filter: FilterPlaceholder,
    width: 110,
  },
  {
    Header: 'Transaction Type',
    accessor: 'TranType',
    width: 80,
  },
  {
    Header: 'Payment Date',
    accessor: d => format(d.PaymentDate, 'MM/DD/YYYY'),
    id: 3,
    sortType: dateSort,
    width: 80,
  },
  {
    Header: 'Amount',
    accessor: 'Amount',
    Cell: ({ row }) => (
      <NumberHandler
        num={row.original.Amount}
        justify="flex-end"
        currency="$"
        float={2}
      />
    ),
    id: 5,
    width: 60,
  },
  {
    Header: 'Payment Type',
    accessor: 'PaymentTypeName',
  },
  {
    Header: 'Account',
    accessor: 'AccountName',
  },
  {
    Header: 'Status',
    accessor: d =>
      safeEval(
        () =>
          paymentStatuses.find(
            paymentStatus => paymentStatus.PaymentStatusId === d.StatusId,
          ).PaymentStatusName,
        'NA',
      ),
    id: 4,
    width: 70,
  },
  {
    Header: 'Amount Mapped',
    accessor: 'AmountMapped',
    Cell: ({ row }) => (
      <NumberHandler
        num={row.original.AmountMapped}
        justify="flex-end"
        currency="$"
        float={2}
      />
    ),
    id: 8,
    width: 80,
  },
  {
    Header: 'Approved By',
    accessor: d => d.ApprovedBy || 'Waiting',
    id: 9,
  },
  {
    width: 40,
    Cell: ({ row }) => (
      <a
        onClick={e => e.stopPropagation()}
        href={`/payments/${row.original.PaymentId}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
          color: '#666',
        }}
      >
        <OpenInNew />
      </a>
    ),
    id: 'newTab',
  },
];

const Payments = () => {
  const [, setQuery] = useQueryParams({
    page: NumberParam,
    rows: NumberParam,
    search: withDefault(StringParam, ''),
    sort: withDefault(JsonParam, []),
  });

  const defaultFilters = storageService.getItem('paymentFilters');
  const { dataStore, authStore, orgStore } = useRootStore();
  const { isIntegrated } = orgStore;
  const history = useHistory();

  const [isLoading, setLoading] = useState(true);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [paymentStatuses, setPaymentStatuses] = useState([]);
  const [filters, setFilters] = useState(defaultFilters || {});

  const payments = dataStore.paymentArray[0];
  const rowInfo = dataStore.paymentArray[1];

  const fetchFilteredPayments = async payload => {
    const filterJson = {
      DateFilter1Type: '>',
      DateFilter2Type: '<',
    };

    if (payload.recordLimit) filterJson.RecordLimit = +payload.recordLimit;
    if (payload.dateFilter1) filterJson.DateFilter1 = payload.dateFilter1;
    if (payload.dateFilter2) filterJson.DateFilter2 = payload.dateFilter2;
    if (payload.transactionType)
      filterJson.TransactionType = payload.transactionType;
    if (payload.paymentType) filterJson.PaymentType = payload.paymentType;
    if (payload.status) filterJson.Status = payload.status;
    if (payload.contact) filterJson.Contact = payload.contact;

    await dataStore.retrieveFilteredPayments(JSON.stringify(filterJson));
  };

  useEffect(() => {
    const fetchPaymentStatuses = async () => {
      try {
        const { data } = await authStore.request.get(
          `${process.env.REACT_APP_API}/api/payment-statuses`,
        );
        setPaymentStatuses(data);
      } catch (e) {
        console.error(e);
      }
    };

    const fetchPaymentTypes = async () => {
      try {
        const { data } = await authStore.request.get(
          `${process.env.REACT_APP_API}/api/payment-types`,
        );
        setPaymentTypes(data);
      } catch (e) {
        console.error(e);
      }
    };

    const initialize = async () => {
      await fetchFilteredPayments(filters);
      await fetchPaymentStatuses();
      await fetchPaymentTypes();

      setLoading(false);
    };
    initialize();
  }, []);

  const onChangeFilter = async updatedFilters => {
    setQuery({ page: 1 });

    setLoading(true);
    setFilters(updatedFilters);
    storageService.setItem('paymentFilters', updatedFilters);
    await fetchFilteredPayments(updatedFilters);
    setLoading(false);
  };

  if (isLoading) return <Spinner />;

  return (
    <Observer
      render={() => {
        return (
          <FlexDiv>
            <WideBox style={{ maxWidth: '1500px', width: '98%' }}>
              <CustomTable
                data={payments}
                columns={columns({ paymentStatuses })}
                minRows={1}
                trClassName="react-table-hover-row"
                getTrProps={({ row }) => ({
                  onClick: () =>
                    history.push(`/payments/${row.original.PaymentId}`),
                })}
                tableHeader={
                  <Typography variant="h2">Invoice Payments</Typography>
                }
                rowInfo={rowInfo[0]}
                actionables={
                  <Fragment>
                    <RenderPropModal
                      manageHandleOpen
                      cardDisplay={({ handleClose }) => (
                        <FilterModal
                          paymentTypes={paymentTypes}
                          paymentStatuses={paymentStatuses}
                          filters={filters}
                          handleClose={handleClose}
                          onChangeFilter={onChangeFilter}
                        />
                      )}
                    >
                      {() => {
                        const foundRows = rowInfo[0]?.FoundRows || 0;
                        const totalRows = rowInfo[0]?.TotalRows || 0;
                        const hasFilters =
                          !isCompareObjects({}, filters) ||
                          foundRows < totalRows;

                        return (
                          <Button
                            style={{
                              marginRight: !isIntegrated ? '10px' : '0px',
                            }}
                            variant="outlined"
                            color={hasFilters ? 'primary' : 'default'}
                          >
                            <FilterIcon
                              style={{ marginRight: '4px', marginLeft: '-3px' }}
                            />
                            {`Filters${hasFilters ? ' (Active)' : ''}`}
                          </Button>
                        );
                      }}
                    </RenderPropModal>

                    {!isIntegrated && (
                      <Button
                        variant="outlined"
                        color="primary"
                        component={Link}
                        to="/payments/new"
                      >
                        <CirclePlusIcon
                          style={{ marginRight: '4px', marginLeft: '-3px' }}
                        />
                        New Payment
                      </Button>
                    )}
                  </Fragment>
                }
                containerStyle={{ padding: '0 2rem' }}
              />
            </WideBox>
          </FlexDiv>
        );
      }}
    />
  );
};

export default Payments;
